import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, Linkedin } from 'lucide-react';
import { contactInfo } from '../mockData';

// WhatsApp Icon Component
const WhatsAppIcon = ({ size = 20, className = "" }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width={size} 
    height={size} 
    viewBox="0 0 24 24" 
    fill="currentColor"
    className={className}
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="text-white">
      {/* Main Footer Content */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info */}
          <div>
            <h3 className="text-2xl font-bold mb-4 text-gradient-gold">
              TRITON
            </h3>
            <p className="text-sm mb-4 text-gray-200">
              Leading merchant exporter of premium Indian spices, pulses, FMCG products, and household items serving global markets.
            </p>
            <p className="text-xs italic" style={{ color: 'var(--gold-light)' }}>
              Bridging Markets, Building Trust
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--gold-accent)' }}>
              Quick Links
            </h4>
            <ul className="space-y-2">
              <li>
                <Link to="/" className="text-sm hover:text-[var(--gold-light)] transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/about" className="text-sm hover:text-[var(--gold-light)] transition-colors">
                  About Us
                </Link>
              </li>
              <li>
                <Link to="/products" className="text-sm hover:text-[var(--gold-light)] transition-colors">
                  Products
                </Link>
              </li>
              <li>
                <Link to="/services" className="text-sm hover:text-[var(--gold-light)] transition-colors">
                  Services
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-sm hover:text-[var(--gold-light)] transition-colors">
                  Contact
                </Link>
              </li>
            </ul>
          </div>

          {/* Product Categories */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--gold-accent)' }}>
              Our Products
            </h4>
            <ul className="space-y-2 text-sm text-gray-200">
              <li>Blended Spices</li>
              <li>Whole & Ground Spices</li>
              <li>Pulses & Grains</li>
              <li>Tea & Coffee</li>
              <li>FMCG Products</li>
              <li>Household Items</li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="text-lg font-semibold mb-4" style={{ color: 'var(--gold-accent)' }}>
              Get In Touch
            </h4>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail size={18} className="mt-1 flex-shrink-0" style={{ color: 'var(--gold-light)' }} />
                <div className="flex flex-col">
                  <a href={`mailto:${contactInfo.email}`} className="text-sm hover:text-[var(--gold-light)] transition-colors">
                    {contactInfo.email}
                  </a>
                  <a href={`mailto:${contactInfo.emailSecondary}`} className="text-sm hover:text-[var(--gold-light)] transition-colors">
                    {contactInfo.emailSecondary}
                  </a>
                </div>
              </li>
              <li className="flex items-start space-x-3">
                <Phone size={18} className="mt-1 flex-shrink-0" style={{ color: 'var(--gold-light)' }} />
                <a href={`tel:${contactInfo.phone}`} className="text-sm hover:text-[var(--gold-light)] transition-colors">
                  {contactInfo.phone}
                </a>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin size={18} className="mt-1 flex-shrink-0" style={{ color: 'var(--gold-light)' }} />
                <span className="text-sm">{contactInfo.address}</span>
              </li>
            </ul>

            {/* Social Media */}
            <div className="flex space-x-4 mt-6">
              <a 
                href="https://wa.me/919819618390" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[#25D366] transition-colors" 
                aria-label="WhatsApp"
                data-testid="whatsapp-link"
              >
                <WhatsAppIcon size={20} />
              </a>
              <a 
                href="mailto:info@tritonglobaltrade.com" 
                className="hover:text-[var(--gold-light)] transition-colors" 
                aria-label="Email"
                data-testid="email-link"
              >
                <Mail size={20} />
              </a>
              <a 
                href="https://in.linkedin.com/in/hasmitapatel5" 
                target="_blank" 
                rel="noopener noreferrer"
                className="hover:text-[var(--gold-light)] transition-colors" 
                aria-label="LinkedIn"
                data-testid="linkedin-link"
              >
                <Linkedin size={20} />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center text-sm text-gray-200">
            <p>© {currentYear} Triton Global Trade. All rights reserved.</p>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="#" className="hover:text-[var(--gold-light)] transition-colors">Privacy Policy</a>
              <a href="#" className="hover:text-[var(--gold-light)] transition-colors">Terms of Service</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
